import React from 'react'
import { Modal } from 'antd'
import { transactionsApi } from 'api/transaction'
import { IAdminMultiOrderResponse } from 'models/orders'
import { OrderLedgerTable } from 'components/orders/TransactionsModal/OrderLedgerTable'
import { showServerError } from 'shared/utils/showServerError'
import {
  getSuperOrderLedger,
  IOrderLedgerSource
} from 'shared/utils/transactions/getOrderLedger'
import styles from './SuperOrderTransactionsModal.module.scss'

interface ISuperOrderTransactionsModalProps {
  open: boolean
  superOrder: IAdminMultiOrderResponse | null
  onClose: () => void
}

export const SuperOrderTransactionsModal: React.FC<
  ISuperOrderTransactionsModalProps
> = ({ open, superOrder, onClose }) => {
  const [sources, setSources] = React.useState<IOrderLedgerSource[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  // Статус заказа в соседней таблице меняет объект супер-заказа, но не состав заказов —
  // историю перезапрашиваем только при открытии и смене состава
  const orders = superOrder?.orders ?? []
  const ordersKey = orders.map(({ _id }) => _id).join(',')

  React.useEffect(() => {
    if (!open || !ordersKey) {
      return
    }

    let isActual = true

    const getTransactions = async () => {
      try {
        setIsLoading(true)
        setSources([])
        // Неполная лента хуже пустой: без одного заказа суммы супер-заказа не сойдутся
        const result = await Promise.all(
          orders.map(async ({ _id, number }) => {
            const [seller, buyer] = await Promise.all([
              transactionsApi.getTransactions({
                skip: '0',
                limit: '500',
                order_id: _id
              }),
              transactionsApi.getOrderTransactions(_id)
            ])
            return {
              orderNumber: number,
              sellerTransactions: seller.data.transactions,
              orderTransactions: buyer.data.transactions
            }
          })
        )
        if (isActual) {
          setSources(result)
        }
      } catch (e) {
        if (isActual) {
          setSources([])
          showServerError(e)
        }
      } finally {
        if (isActual) {
          setIsLoading(false)
        }
      }
    }

    getTransactions()

    return () => {
      isActual = false
    }
  }, [open, ordersKey])

  const ledger = React.useMemo(() => getSuperOrderLedger(sources), [sources])

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        superOrder
          ? `Транзакции супер-заказа № ${superOrder.number}`
          : 'Транзакции супер-заказа'
      }
      wrapClassName={styles.wrapper}
      className={styles.modal}
    >
      <OrderLedgerTable rows={ledger} isLoading={isLoading} withOrderNumber />
    </Modal>
  )
}
